'use client';
import { useTranslations } from 'next-intl';
import { type FC, type ReactNode, useState } from 'react';
import { BaseLineIntelligentAspectRatio } from '@/app/components/icons/BaseLineIntelligentAspectRatio';
import { ProductLine1 } from '@/app/components/icons/ProductLine1';
import { ProductLine3 } from '@/app/components/icons/ProductLine3';
import { ProductLine4 } from '@/app/components/icons/ProductLine4';
import { ProductLine9 } from '@/app/components/icons/ProductLine9';
import { ProductLine16 } from '@/app/components/icons/ProductLine16';
import { ProductLine21 } from '@/app/components/icons/ProductLine21';
import { CommonSelect, type SelectOption } from '@/app/components/shared/CommonSelect';
import { CustomAspectRatioDialog } from '@/app/components/shared/CustomAspectRatioDialog';
import type { ModelVersion, OptionPropsWithType } from '@/app/types';
import {
  CUSTOM_ASPECT_RATIO_VALUE,
  getAvailableOptions,
  isCustomAspectRatioValue,
  parseCustomAspectRatio,
} from '@/app/utils';

/**
 * 宽高比选项组件
 * 用于设置视频的宽高比（如16:9, 9:16, 1:1）
 *
 * @param value 当前选中的值，格式为 "wide:high" 或预设值（如 "16:9"）
 * @param onChange 值变更回调
 */
export const AspectRatioOption: FC<
  OptionPropsWithType<string> & { className?: string; modelVersion: ModelVersion }
> = ({ value, onChange, type, className, modelVersion }) => {
  const t = useTranslations();
  const [customDialogOpen, setCustomDialogOpen] = useState(false);

  const iconMap: Record<string, ReactNode> = {
    '16:9': <ProductLine16 className="size-6" />,
    '21:9': <ProductLine21 className="size-6" />,
    '9:16': <ProductLine9 className="size-6" />,
    '9:21': <ProductLine21 className="size-6 rotate-90" />,
    '1:1': <ProductLine1 className="size-6" />,
    '4:3': <ProductLine4 className="size-6" />,
    '3:4': <ProductLine3 className="size-6" />,
    intelligent: <BaseLineIntelligentAspectRatio className="size-6" />,
  };

  const availableRatios = getAvailableOptions(modelVersion, type).aspect_ratio || [];

  // 检查当前值是否为自定义宽高比（不在预设列表里的具体比例值）
  const isCustomValue = isCustomAspectRatioValue(value, availableRatios);

  // 解析自定义值用于显示
  const getCustomLabel = (): string => {
    if (isCustomValue && value) {
      return t('create.createForm.settings.aspectRatio.optionCustom.textWithValue', { value });
    }
    return t('create.createForm.settings.aspectRatio.optionCustom.text');
  };

  const options: SelectOption[] = availableRatios.map((ar) => {
    switch (ar) {
      case CUSTOM_ASPECT_RATIO_VALUE:
        return {
          value: CUSTOM_ASPECT_RATIO_VALUE,
          label: getCustomLabel(),
          onItemClick: () => {
            setCustomDialogOpen(true);
          }, // 只要点击自定义选项，无论 value 是否改变都会打开弹窗
        };
      case 'intelligent':
        return {
          value: ar,
          label: t('create.createForm.settings.aspectRatio.optionIntelligent.text'),
          icon: iconMap[ar],
        };
      default:
        return {
          value: ar,
          label: ar,
          icon: iconMap[ar] || undefined,
        };
    }
  });

  // 如果当前值是自定义值，显示为 "custom" 选项
  const displayValue = isCustomValue ? CUSTOM_ASPECT_RATIO_VALUE : value || '';

  const handleValueChange = (newValue: string) => {
    if (newValue !== CUSTOM_ASPECT_RATIO_VALUE) {
      onChange(newValue);
    }
  };

  const handleCustomConfirm = (wide: number, high: number) => {
    const customRatio = `${wide}:${high}`;
    onChange(customRatio);
  };

  const { wide: initialWide, high: initialHigh } = parseCustomAspectRatio(isCustomValue ? value : undefined);

  return (
    <>
      <CommonSelect value={displayValue} options={options} onValueChange={handleValueChange} className={className} />
      <CustomAspectRatioDialog
        open={customDialogOpen}
        onOpenChange={setCustomDialogOpen}
        onConfirm={handleCustomConfirm}
        initialWide={initialWide}
        initialHigh={initialHigh}
      />
    </>
  );
};
